import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider
} from "@mui/material";
import axios from "axios";

const cellBorder = "1px solid #dcdcdc";
const labelStyle = { fontWeight: 600 };
const sectionHeader = {
  backgroundColor: "#f5f7fa",
  fontWeight: 700
};

const AssessmentSummaryNew = ({ data, appId, assessorId, assessmentId }) => {
  const [naf6Data, setNaf6Data] = useState(null);

  useEffect(() => {
    const fetchNAF6Data = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/naf6/data/${appId}/${assessorId}/${assessmentId}`
        );
        if (response.data.success) {
          setNaf6Data(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching NAF6 data:", error);
      }
    };

    if (appId && assessorId && assessmentId) {
      fetchNAF6Data();
    }
  }, [appId, assessorId, assessmentId]);

  if (!data) return null;

  const renderMultiSelectField = (value) => {
    if (!value) return "";
    return value.split("~").map((item, idx) => (
      <li key={idx}>{item}</li>
    ));
  };

  const renderMultiSelectFieldArray = (value) => {
    if (!value) return null;
    let arr = value;
    if (typeof value === "string") {
      try {
        arr = JSON.parse(value);
      } catch (e) {
        return null;
      }
    }
    if (!Array.isArray(arr)) return null;
    return arr.map((item, idx) => (
      <li key={idx} style={{ color: getColor(item.color) }}>
        {item.text}
      </li>
    ));
  };

const getColor = (color) => {
  switch (color) {
    case "success":
      return "green";
    case "error":
      return "red";
    case "warning":
      return "orange";
    case "info":
      return "blue";
    case "secondary":
      return "purple";
    default:
      return "black";
  }
};

  return (
    <Box sx={{ mb: 4 }}>
      <Card elevation={3}>
        {/* HEADER */}
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "#fff",
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            SUMMARY OF THE ASSESSMENT
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {/* LAB NAME */}
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: cellBorder, ...labelStyle }}
                >
                  {data.lblNAF6LabName || "Laboratory Assessed"}
                </TableCell>
                <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  {data.lblNaf6Lab}
                </TableCell>
              </TableRow>

              {/* QM + DATE */}
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell sx={{ border: cellBorder }}>
                  <strong>Date(s) of Visit:</strong> {data.lblNaf6Date}
                </TableCell>
                {/* <TableCell colSpan={2} sx={{ border: cellBorder }}>
                  <strong>Corrective Action Submission Date:</strong>{" "}
                  {data.lblsubmissionDate? new Date(data.lblsubmissionDate).toLocaleDateString("en-GB") : "-"}
                </TableCell> */}
              </TableRow>

              {/* TYPE */}
              <TableRow>
                <TableCell colSpan={5} sx={{ border: cellBorder }}>
                  <strong>Type of Visit:</strong> {data.lblNaf6AssessmenttYPE}
                </TableCell>
              </TableRow>

              {/* FIELD / DISCIPLINE */}
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell colSpan={2} sx={{ border: cellBorder }}>
                  <strong>Field:</strong> {data.lblNaf6field}
                </TableCell>
                <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  <strong>Discipline(s):</strong> {data.lblNaf6Discipline}
                </TableCell>
              </TableRow>

              {/* FACILITY */}
              <TableRow>
                <TableCell colSpan={5} sx={{ border: cellBorder }}>
                  <strong>Facility(s):</strong> {data.lblNaf6Fcility}
                </TableCell>
              </TableRow>

              {/* LEAD ASSESSOR */}
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell colSpan={5} sx={{ border: cellBorder }}>
                  <strong>Lead Assessor:</strong> {data.lblNaf6LA}
                </TableCell>
              </TableRow>

              {/* ASSESSMENT TEAM */}
              {data.rptrNaf6?.map((assessor, i) => (
                <TableRow key={i}>
                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{ border: cellBorder, fontWeight: 600 }}
                  >
                    {assessor.Capacity}
                  </TableCell>
                  <TableCell colSpan={3} sx={{ border: cellBorder }}>
                    {assessor.Name}
                  </TableCell>
                </TableRow>
              ))}

              {/* NAF6 DATA SECTION */}
              {naf6Data && (
                <>
                  {/* Operation Details */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      ASSESSMENT SUMMARY
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Operation
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.operation || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Accreditation Status
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.accreditationStatus || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Daily Work Load
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.dailyWorkLoad || ""}
                    </TableCell>
                  </TableRow>
                  {naf6Data.SCF && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      SCF
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.SCF || ""}
                    </TableCell>
                  </TableRow>
                  )}
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Hospital Collection Service
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.hospitalCollectionService || ""}
                    </TableCell>
                  </TableRow>
                  {naf6Data.dates && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ border: cellBorder, ...labelStyle }}
                      >
                        Date of earlier visit
                      </TableCell>
                      <TableCell colSpan={3} sx={{ border: cellBorder }}>
                        {naf6Data.dates
                          ? new Date(naf6Data.dates).toLocaleDateString("en-GB")
                          : ""}
                      </TableCell>
                    </TableRow>
                  )}
                  {naf6Data.nonConformities && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ border: cellBorder, ...labelStyle }}
                      >
                        Non-Conformities during earlier onsite assessment have/
                        have not been discharged
                      </TableCell>
                      <TableCell colSpan={3} sx={{ border: cellBorder }}>
                        {naf6Data.nonConformities || ""}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Compliance and Processes */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      COMPLIANCE & PROCESSES
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Overall Compliance
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.overallComplianceArray,
                        )}
                      </ul>
                      {naf6Data.overallComplianceRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.overallComplianceRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Pre-Examination Processes
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.preExaminationProcessesArray,
                        )}
                      </ul>
                      {naf6Data.preExaminationProcessesRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.preExaminationProcessesRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Infrastructure & Equipment */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      INFRASTRUCTURE & EQUIPMENT
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Infrastructure
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.infrastructureArray,
                        )}
                      </ul>
                      {naf6Data.infrastructureRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.infrastructureRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Equipment
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(naf6Data.equipmentArray)}
                      </ul>
                      {naf6Data.equipmentRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.equipmentRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Staff & Signatories */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      STAFF & SIGNATORIES
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Laboratory Staff
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.laboratoryStaffArray,
                        )}
                      </ul>
                      {naf6Data.laboratoryStaffRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.laboratoryStaffRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Authorized Signatories
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.authorizedSignatoriesArray,
                        )}
                      </ul>
                      {naf6Data.authorizedSignatoriesRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.authorizedSignatoriesRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Quality Control */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      QUALITY CONTROL & CALIBRATION
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Calibration Procedures
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.calibrationProceduresArray,
                        )}
                      </ul>
                      {naf6Data.calibrationProceduresRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.calibrationProceduresRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      IQC (Internal Quality Control)
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(naf6Data.IQCArray)}
                      </ul>
                      {naf6Data.IQCRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.IQCRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      EQAS (External Quality Assessment)
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(naf6Data.EQASArray)}
                      </ul>
                      {naf6Data.EQASRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.EQASRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Reporting & Indicators */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      REPORTING & QUALITY INDICATORS
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Reporting Results
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.reportingResultsArray,
                        )}
                      </ul>
                      {naf6Data.reportingResultsRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.reportingResultsRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Quality Indicators
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.qualityIndicatorsArray,
                        )}
                      </ul>
                      {naf6Data.qualityIndicatorsRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.qualityIndicatorsRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Feedback & Non-Conformance */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      FEEDBACK & NON-CONFORMANCE MANAGEMENT
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Complaints & Feedback
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.complaintsFeedbackArray,
                        )}
                      </ul>
                      {naf6Data.complaintsFeedbackRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.complaintsFeedbackRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Non-Conformance Handling
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.nonConformanceHandlingArray,
                        )}
                      </ul>
                      {naf6Data.nonConformanceHandlingRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.nonConformanceHandlingRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Risk Management & LIS */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      RISK MANAGEMENT & LABORATORY INFORMATION SYSTEM
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Risk Management
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.riskManagementArray,
                        )}
                      </ul>
                      {naf6Data.riskManagementRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.riskManagementRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      LIS & Auto-Verification
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.LISAutoVerificationArray,
                        )}
                      </ul>
                      {naf6Data.LISAutoVerificationRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.LISAutoVerificationRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Lab Safety */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      LABORATORY SAFETY
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Lab Safety
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(naf6Data.labSafetyArray)}
                      </ul>
                      {naf6Data.labSafetyRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.labSafetyRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Strengths & Improvements */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      STRENGTHS & AREAS FOR IMPROVEMENT
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Areas of Strength
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.areasStrengthArray,
                        )}
                      </ul>
                      {naf6Data.areasStrengthRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.areasStrengthRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Areas for Improvement
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {renderMultiSelectFieldArray(
                          naf6Data.areasforImprovementArray,
                        )}
                      </ul>
                      {naf6Data.areasforImprovementRemark && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "#666" }}>
                          Remark: {naf6Data.areasforImprovementRemark || ""}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* COMPLIANCE */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      Compliance to other NABL documents
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>
                          NABL 133:{" "}
                          {naf6Data.nabl133Value === "Non Compliance" ? (
                            <span style={{ color: "red" }}>
                              {naf6Data.nabl133Value}
                            </span>
                          ) : (
                            naf6Data.nabl133Value
                          )}
                        </li>
                        {naf6Data.nabl133Value &&
                          naf6Data.nabl133Value === "Non Compliance" && (
                            <li>
                              Remarks for Non-Compliance NABL 133:{" "}
                              {naf6Data.nabl133NcStatement || ""}
                            </li>
                          )}
                        <li>
                          NABL 142:{" "}
                          {naf6Data.nabl142Value === "Non Compliance" ? (
                            <span style={{ color: "red" }}>
                              {naf6Data.nabl142Value}
                            </span>
                          ) : (
                            naf6Data.nabl142Value
                          )}
                        </li>
                        {naf6Data.nabl142Value &&
                          naf6Data.nabl142Value === "Non Compliance" && (
                            <li>
                              Remarks for Non-Compliance NABL 142:{" "}
                              {naf6Data.nabl142NcStatement || ""}
                            </li>
                          )}
                        <li>
                          NABL 163:{" "}
                          {naf6Data.nabl163Value === "Non Compliance" ? (
                            <span style={{ color: "red" }}>
                              {naf6Data.nabl163Value}
                            </span>
                          ) : (
                            naf6Data.nabl163Value
                          )}
                        </li>
                        {naf6Data.nabl163Value &&
                          naf6Data.nabl163Value === "Non Compliance" && (
                            <li>
                              Remarks for Non-Compliance NABL 163:{" "}
                              {naf6Data.nabl163NcStatement || ""}
                            </li>
                          )}
                        {naf6Data.nabl112a && (
                          <>
                            <li>
                              NABL 112a:{" "}
                              {naf6Data.nabl112a === "Non Compliance" ? (
                                <span style={{ color: "red" }}>
                                  {naf6Data.nabl112a}
                                </span>
                              ) : (
                                naf6Data.nabl112a
                              )}
                            </li>
                            {naf6Data.nabl112a &&
                              naf6Data.nabl112a === "Non Compliance" && (
                                <li>
                                  Remarks for Non-Compliance NABL 112a:{" "}
                                  {naf6Data.nabl112ANcStatement || ""}
                                </li>
                              )}
                          </>
                        )}
                        {naf6Data.nabl135 && (
                          <>
                            <li>
                              NABL 135:{" "}
                              {naf6Data.nabl135 === "Non Compliance" ? (
                                <span style={{ color: "red" }}>
                                  {naf6Data.nabl135}
                                </span>
                              ) : (
                                naf6Data.nabl135
                              )}
                            </li>
                            {naf6Data.nabl135 &&
                              naf6Data.nabl135 === "Non Compliance" && (
                                <li>
                                  Remarks for Non-Compliance NABL 135:{" "}
                                  {naf6Data.nabl135NcStatement || ""}
                                </li>
                              )}
                          </>
                        )}
                      </ul>
                    </TableCell>
                  </TableRow>
                  {/* Internal Audit Information */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      INTERNAL AUDIT INFORMATION
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Trained Auditor
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      sx={{
                        border: cellBorder,
                        color:
                          naf6Data?.trainedAuditor === "No" ? "red" : "inherit",
                      }}
                    >
                      {naf6Data?.trainedAuditor || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        border: cellBorder,
                        ...labelStyle,
                        color:
                          naf6Data?.independenceOfActivity === "No"
                            ? "red"
                            : "inherit",
                      }}
                    >
                      Independence of Activity
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data?.independenceOfActivity || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Frequency of Audit
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.frequencyOfAudit || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      All Points Covered
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      sx={{
                        border: cellBorder,
                        color:
                          naf6Data?.allPointsCovered === "No"
                            ? "red"
                            : "inherit",
                      }}
                    >
                      {naf6Data?.allPointsCovered || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Conducted Within Year
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      sx={{
                        border: cellBorder,
                        color:
                          naf6Data?.conductedWithinYear === "No"
                            ? "red"
                            : "inherit",
                      }}
                    >
                      {naf6Data?.conductedWithinYear || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Audit Comments
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.auditComments || ""}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      Management Comments
                    </TableCell>
                    <TableCell colSpan={3} sx={{ border: cellBorder }}>
                      {naf6Data.managementComments || ""}
                    </TableCell>
                  </TableRow>

                  {/* PT & ILC Information */}
                  <TableRow sx={sectionHeader}>
                    <TableCell colSpan={5} sx={{ border: cellBorder }}>
                      PROFICIENCY TESTING (PT) & ILC INFORMATION
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ border: cellBorder, ...labelStyle }}
                    >
                      PT Availability
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      sx={{
                        border: cellBorder,
                        color:
                          naf6Data?.ptAvailability === "No" ? "red" : "inherit",
                      }}
                    >
                      {naf6Data?.ptAvailability || ""}
                    </TableCell>
                  </TableRow>
                  {naf6Data?.ptAvailability === "No" && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ border: cellBorder, ...labelStyle }}
                      >
                        Reason for PT not being available
                      </TableCell>
                      <TableCell colSpan={3} sx={{ border: cellBorder }}>
                        {naf6Data?.ptNotAvailableReason || ""}
                      </TableCell>
                    </TableRow>
                  )}
                  {naf6Data?.ptAvailability === "Yes" && (
                    <>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ border: cellBorder, ...labelStyle }}
                        >
                          PT Coverage of Scope
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            border: cellBorder,
                            color:
                              naf6Data?.ptCoverageOfScope === "Not Adequate"
                                ? "red"
                                : "inherit",
                          }}
                        >
                          {naf6Data?.ptCoverageOfScope || ""}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ border: cellBorder, ...labelStyle }}
                        >
                          PT Outlier
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            border: cellBorder,
                            color:
                              naf6Data?.ptOutlier === "Yes" ? "red" : "inherit",
                          }}
                        >
                          {naf6Data?.ptOutlier || ""}
                          {naf6Data?.ptOutlierRemarks && (
                            <Box
                              sx={{ mt: 1, fontStyle: "italic", color: "#666" }}
                            >
                              Remarks: {naf6Data?.ptOutlierRemarks || ""}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ border: cellBorder, ...labelStyle }}
                        >
                          ILC Coverage of Scope
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            border: cellBorder,
                            color:
                              naf6Data?.ilcCoverageOfScope === "Not Adequate"
                                ? "red"
                                : "inherit",
                          }}
                        >
                          {naf6Data?.ilcCoverageOfScope || ""}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ border: cellBorder, ...labelStyle }}
                        >
                          ILC Outlier
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            border: cellBorder,
                            color:
                              naf6Data?.ilcOutlier === "Yes"
                                ? "red"
                                : "inherit",
                          }}
                        >
                          {naf6Data?.ilcOutlier || ""}
                          {naf6Data?.ilcOutlierRemarks && (
                            <Box
                              sx={{ mt: 1, fontStyle: "italic", color: "#666" }}
                            >
                              Remarks: {naf6Data?.ilcOutlierRemarks || ""}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ border: cellBorder, ...labelStyle }}
                        >
                          Other Quality Measures
                        </TableCell>
                        <TableCell colSpan={3} sx={{ border: cellBorder }}>
                          {naf6Data.otherQualityMeasures || ""}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </>
              )}
              {/* Overall Recommendation of Assessment Team */}
              <TableRow sx={sectionHeader}>
                <TableCell colSpan={5} sx={{ border: cellBorder }}>
                  Overall recommendation of Assessment team
                </TableCell>
              </TableRow>
              {naf6Data?.isLabRecommend && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: cellBorder, ...labelStyle }}
                >
                  Is this lab Recommended for Accreditation
                </TableCell>
                <TableCell
                  colSpan={3}
                  sx={{
                    border: cellBorder,
                    color:
                      naf6Data?.isLabRecommend === "No" ? "red" : "inherit",
                  }}
                >
                  {naf6Data?.isLabRecommend || ""}
                </TableCell>
              </TableRow>
              )}
              {naf6Data?.assessmentSummary && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: cellBorder, ...labelStyle }}
                >
                  Assessment Summary
                </TableCell>
                <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  {naf6Data?.assessmentSummary || ""}
                </TableCell>
              </TableRow>
              )}
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: cellBorder, ...labelStyle }}
                >
                  Recommendations of Assessment Team
                </TableCell>
                <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  {naf6Data?.recommendationsOfAssessmentTeam || ""}
                </TableCell>
              </TableRow>
              {/* NC Raised */}
              <TableRow sx={sectionHeader}>
                <TableCell colSpan={5} sx={{ border: cellBorder }}>
                  NC Raised
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: cellBorder, ...labelStyle }}
                >
                  Number of NCs
                </TableCell>
                <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  {naf6Data?.numberOfNCs || "0"}
                </TableCell>
                {/* <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  {`${naf6Data?.numberOfNCs || 0}, 
                   (Clause 4 - ${naf6Data?.ncClause4 || 0},
                   Clause 5 - ${naf6Data?.ncClause5 || 0}, 
                   Clause 6 - ${naf6Data?.ncClause6 || 0}, 
                   Clause 7 - ${naf6Data?.ncClause7 || 0}, 
                   Clause 8 - ${naf6Data?.ncClause8 || 0})`}
                </TableCell> */}
              </TableRow>

              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: cellBorder, ...labelStyle }}
                >
                  All NCs Closed Date
                </TableCell>
                <TableCell colSpan={3} sx={{ border: cellBorder }}>
                  {naf6Data?.allNCsClosedDate
                    ? new Date(naf6Data.allNCsClosedDate).toLocaleDateString(
                        "en-GB",
                      )
                    : ""}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default AssessmentSummaryNew;
